<script setup>
/**
 * Aba "Agendadas" — visão unificada LEVE (G3 / Fatia 2).
 * Dois motores continuam separados: só leitura + cancelar/link.
 * Fontes: campanhas one_off com scheduled_at futuro + ScheduledMessage pendentes.
 * API: AccountScheduledMessagesAPI (account-level, já existente).
 * Não grava métricas de campanha.
 */
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { getInboxIconByType, INBOX_TYPES } from 'dashboard/helper/inbox';
import AccountScheduledMessagesAPI from 'dashboard/api/accountScheduledMessages';
import ConversationApi from 'dashboard/api/inbox/conversation';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const { t } = useI18n();
const store = useStore();
const router = useRouter();
const { accountScopedRoute } = useAccount();
const { isAdmin } = useAdmin();

const loadingMessages = ref(true);
const scheduledMessages = ref([]);
const expandedKeys = ref([]);
const cancelDialogRef = ref(null);
const itemToCancel = ref(null);
const cancelling = ref(false);

const uiFlags = useMapGetter('campaigns/getUIFlags');
const allCampaigns = useMapGetter('campaigns/getAllCampaigns');
const accountId = useMapGetter('getCurrentAccountId');
const inboxes = useMapGetter('inboxes/getInboxes');

const isLoading = computed(
  () => loadingMessages.value || uiFlags.value?.isFetching
);

const nowUnix = () => Math.floor(Date.now() / 1000);

const isFutureScheduledCampaign = campaign => {
  if (!campaign || campaign.campaign_type !== 'one_off') return false;
  if (!campaign.scheduled_at) return false;
  if (Number(campaign.scheduled_at) <= nowUnix()) return false;
  if (campaign.campaign_status === 'completed') return false;
  // Drip já cancelado/concluído/em execução não entra na aba de futuros.
  if (
    campaign.is_drip &&
    ['cancelled', 'completed', 'running', 'failed', 'paused'].includes(
      campaign.drip_status
    )
  ) {
    return false;
  }
  return true;
};

const inboxById = id => (inboxes.value || []).find(i => i.id === Number(id));

const channelLabel = inbox => {
  if (!inbox) return '';
  const type = inbox.channel_type;
  if (type === INBOX_TYPES.WHATSAPP || type === INBOX_TYPES.WHATSAPP_WEB) {
    return t('SIDEBAR.WHATSAPP');
  }
  if (type === INBOX_TYPES.SMS || type === INBOX_TYPES.TWILIO) {
    return t('SIDEBAR.SMS');
  }
  if (type === INBOX_TYPES.API) {
    return t('SIDEBAR.WHATSAPP');
  }
  if (type === INBOX_TYPES.WEB) {
    return t('SIDEBAR.LIVE_CHAT');
  }
  return inbox.name || '';
};

const campaignRouteName = campaign => {
  const type = campaign?.inbox?.channel_type;
  if (type === INBOX_TYPES.SMS || type === INBOX_TYPES.TWILIO) {
    return 'campaigns_sms_index';
  }
  if (type === INBOX_TYPES.WEB) {
    return 'campaigns_livechat_index';
  }
  return 'campaigns_whatsapp_index';
};

const formatWhen = ts => {
  if (!ts) return '';
  const date = new Date(Number(ts) * 1000);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const campaignStatusLabel = campaign => {
  if (!campaign.is_drip) {
    return t('CAMPAIGN.SMS.CARD.STATUS.SCHEDULED');
  }
  // Chaves estáticas (eslint @intlify/vue-i18n/no-dynamic-keys).
  switch (campaign.drip_status) {
    case 'draft':
      return t('CAMPAIGN.DRIP.STATUS.DRAFT');
    case 'running':
      return t('CAMPAIGN.DRIP.STATUS.RUNNING');
    case 'paused':
      return t('CAMPAIGN.DRIP.STATUS.PAUSED');
    case 'completed':
      return t('CAMPAIGN.DRIP.STATUS.COMPLETED');
    case 'failed':
      return t('CAMPAIGN.DRIP.STATUS.FAILED');
    case 'cancelled':
      return t('CAMPAIGN.DRIP.STATUS.CANCELLED');
    case 'scheduled':
    default:
      return t('CAMPAIGN.DRIP.STATUS.SCHEDULED');
  }
};

const campaignItems = computed(() => {
  const list = (allCampaigns.value || []).filter(isFutureScheduledCampaign);
  return list.map(campaign => ({
    key: `campaign-${campaign.id}`,
    kind: 'campaign',
    id: campaign.id,
    title: campaign.title,
    preview: campaign.message,
    scheduledAt: Number(campaign.scheduled_at),
    statusLabel: campaignStatusLabel(campaign),
    meta: [channelLabel(campaign.inbox), campaign.inbox?.name]
      .filter(Boolean)
      .join(' · '),
    inbox: campaign.inbox,
    icon: getInboxIconByType(
      campaign.inbox?.channel_type,
      campaign.inbox?.medium
    ),
    source: campaign,
  }));
});

const messageItems = computed(() => {
  return (scheduledMessages.value || [])
    .filter(msg => msg.status === 'pending')
    .map(msg => {
      const inbox = inboxById(msg.inbox_id);
      const contact =
        msg.contact_name ||
        msg.contact_email ||
        t('KANBAN.ITEM_CONVERSATION.NO_CONTACT'); // same key as ScheduledMessagesTab
      return {
        key: `message-${msg.id}`,
        kind: 'message',
        id: msg.id,
        title: msg.title || t('SCHEDULE_MESSAGES.LIST.MESSAGE_LABEL'),
        preview: msg.message,
        scheduledAt: Number(msg.scheduled_at),
        statusLabel: t('SCHEDULE_MESSAGES.STATUS.PENDING'),
        meta: [
          contact,
          inbox?.name || t('SCHEDULE_MESSAGES.ERROR.INBOX_NOT_FOUND'),
          msg.conversation_display_id != null
            ? `#${msg.conversation_display_id}`
            : null,
        ]
          .filter(Boolean)
          .join(' · '),
        inbox,
        icon: inbox
          ? getInboxIconByType(inbox.channel_type, inbox.medium)
          : 'i-ri-chat-1-line',
        source: msg,
      };
    });
});

const unifiedItems = computed(() => {
  return [...campaignItems.value, ...messageItems.value].sort(
    (a, b) => a.scheduledAt - b.scheduledAt
  );
});

const totalCount = computed(() => unifiedItems.value.length);

const fetchMessages = async () => {
  loadingMessages.value = true;
  try {
    // Só as pendentes aparecem aqui: sem o filtro vinha todo o histórico.
    const res = await AccountScheduledMessagesAPI.get({ status: 'pending' });
    scheduledMessages.value = res.data?.payload || [];
  } catch (err) {
    useAlert(t('SCHEDULE_MESSAGES.ERROR.LOAD'));
    scheduledMessages.value = [];
  } finally {
    loadingMessages.value = false;
  }
};

const refresh = async () => {
  await Promise.all([store.dispatch('campaigns/get'), fetchMessages()]);
};

const toggleExpand = key => {
  const i = expandedKeys.value.indexOf(key);
  if (i === -1) expandedKeys.value.push(key);
  else expandedKeys.value.splice(i, 1);
};

const isExpanded = key => expandedKeys.value.includes(key);

// O texto diz o que a ação faz de verdade em cada tipo: a mensagem agendada
// some, o disparo em massa fica cancelado (histórico preservado) e a campanha
// clássica é apagada, que é como ela deixa de ser enviada.
const cancelKind = item => {
  if (!item) return 'message';
  if (item.kind === 'message') return 'message';
  return item.source?.is_drip ? 'drip' : 'campaign';
};

const cancelContactName = item =>
  item?.source?.contact_name ||
  item?.source?.contact_email ||
  t('KANBAN.ITEM_CONVERSATION.NO_CONTACT');

const cancelDialogCopy = computed(() => {
  const item = itemToCancel.value;
  // Chaves estáticas (eslint @intlify/vue-i18n/no-dynamic-keys).
  switch (cancelKind(item)) {
    case 'drip':
      return {
        title: t('CAMPAIGN.SCHEDULED.CANCEL_DIALOG.DRIP_TITLE', {
          title: item?.title || '',
        }),
        description: t('CAMPAIGN.SCHEDULED.CANCEL_DIALOG.DRIP_DESCRIPTION'),
        confirm: t('CAMPAIGN.SCHEDULED.CANCEL_DIALOG.DRIP_CONFIRM'),
      };
    case 'campaign':
      return {
        title: t('CAMPAIGN.SCHEDULED.CANCEL_DIALOG.CAMPAIGN_TITLE', {
          title: item?.title || '',
        }),
        description: t('CAMPAIGN.SCHEDULED.CANCEL_DIALOG.CAMPAIGN_DESCRIPTION'),
        confirm: t('CAMPAIGN.SCHEDULED.CANCEL_DIALOG.CAMPAIGN_CONFIRM'),
      };
    case 'message':
    default:
      return {
        title: t('CAMPAIGN.SCHEDULED.CANCEL_DIALOG.MESSAGE_TITLE', {
          contact: cancelContactName(item),
        }),
        description: t('CAMPAIGN.SCHEDULED.CANCEL_DIALOG.MESSAGE_DESCRIPTION'),
        confirm: t('CAMPAIGN.SCHEDULED.CANCEL_DIALOG.MESSAGE_CONFIRM'),
      };
  }
});

// Campanha única (SMS ou modelo de WhatsApp) não tem "cancelar": a ação apaga
// a campanha inteira. O botão da linha diz isso com todas as letras.
const deletesCampaign = item =>
  item?.kind === 'campaign' && !item.source?.is_drip;

const cancelActionLabel = item =>
  deletesCampaign(item)
    ? t('CAMPAIGN.SCHEDULED.DELETE_ONE_OFF.ACTION')
    : t('CAMPAIGN.DRIP.ACTIONS.CANCEL');

const openCancelModal = item => {
  itemToCancel.value = item;
  cancelDialogRef.value?.open();
};

const closeCancelModal = () => {
  itemToCancel.value = null;
};

const confirmCancel = async () => {
  const item = itemToCancel.value;
  if (!item || cancelling.value) return;
  cancelling.value = true;
  try {
    if (item.kind === 'message') {
      const msg = item.source;
      if (!msg?.conversation_display_id) {
        useAlert(t('SCHEDULE_MESSAGES.ERROR.DELETE'));
        return;
      }
      await ConversationApi.deleteScheduledMessage(
        msg.conversation_display_id,
        msg.id
      );
      useAlert(t('SCHEDULE_MESSAGES.SUCCESS.DELETE'));
    } else if (item.source?.is_drip) {
      await store.dispatch('campaigns/cancelDrip', item.id);
      useAlert(t('CAMPAIGN.DRIP.ALERTS.CANCEL_SUCCESS'));
    } else {
      // one_off clássico (SMS/WA template): cancelar = remover o agendamento.
      await store.dispatch('campaigns/delete', item.id);
      useAlert(t('CAMPAIGN.CONFIRM_DELETE.API.SUCCESS_MESSAGE'));
    }
    cancelDialogRef.value?.close();
    closeCancelModal();
    await refresh();
  } catch (err) {
    if (item.kind === 'message') {
      useAlert(t('SCHEDULE_MESSAGES.ERROR.DELETE'));
    } else if (item.source?.is_drip) {
      useAlert(t('CAMPAIGN.DRIP.ALERTS.CANCEL_ERROR'));
    } else {
      useAlert(t('CAMPAIGN.CONFIRM_DELETE.API.ERROR_MESSAGE'));
    }
  } finally {
    cancelling.value = false;
  }
};

const goToOrigin = item => {
  if (item.kind === 'message') {
    const msg = item.source;
    if (!msg?.conversation_display_id || !accountId.value) return;
    router.push({
      name: 'inbox_conversation',
      params: {
        accountId: accountId.value,
        inboxId: msg.inbox_id,
        conversation_id: msg.conversation_display_id,
      },
    });
    return;
  }
  router.push(accountScopedRoute(campaignRouteName(item.source)));
};

const canCancel = item => {
  if (item.kind === 'message') return true;
  if (!isAdmin.value) return false;
  if (item.source?.is_drip) {
    return ['draft', 'scheduled', null, undefined].includes(
      item.source.drip_status
    );
  }
  return true;
};

onMounted(async () => {
  // Campanhas: quem carrega é o CampaignsPageRouteView (pai desta rota). O
  // onMounted do filho roda antes do dele, então buscar aqui duplicava o GET.
  if (!inboxes.value?.length) {
    store.dispatch('inboxes/get');
  }
  await fetchMessages();
});
</script>

<template>
  <section class="flex flex-col w-full h-full overflow-hidden bg-n-surface-1">
    <header class="sticky top-0 z-10 px-6">
      <div class="w-full max-w-5xl mx-auto">
        <div class="flex items-center justify-between w-full h-20 gap-2">
          <div class="flex items-center gap-3 min-w-0">
            <h1 class="text-heading-1 text-n-slate-12 truncate">
              {{ t('CAMPAIGN.SCHEDULED.HEADER_TITLE') }}
            </h1>
            <span
              class="inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-n-alpha-2 text-[12px] font-bold text-n-slate-11 tabular-nums"
            >
              {{ totalCount }}
            </span>
          </div>
          <Button
            variant="faded"
            color="slate"
            size="sm"
            icon="i-ri-refresh-line"
            :aria-label="t('CAMPAIGN.SCHEDULED.REFRESH')"
            :title="t('CAMPAIGN.SCHEDULED.REFRESH')"
            :is-loading="isLoading"
            @click="refresh"
          />
        </div>
      </div>
    </header>

    <main class="flex-1 px-6 overflow-y-auto">
      <div class="w-full max-w-5xl mx-auto py-4">
        <div
          v-if="isLoading"
          class="flex items-center justify-center py-16 text-n-slate-11"
        >
          <Spinner />
        </div>

        <div
          v-else-if="!unifiedItems.length"
          class="flex flex-col items-center justify-center gap-2 py-16 text-center"
        >
          <span
            class="i-ri-calendar-schedule-line size-12 text-n-blue-10 mb-2"
          />
          <p class="text-sm font-medium text-n-slate-12">
            {{ t('SCHEDULE_MESSAGES.EMPTY') }}
          </p>
          <p class="text-sm text-n-slate-11">
            {{ t('SCHEDULE_MESSAGES.EMPTY_SUBTEXT') }}
          </p>
        </div>

        <div v-else class="flex flex-col gap-2.5">
          <p
            class="text-[13px] font-medium uppercase tracking-wide text-n-slate-11 mb-1"
          >
            {{ t('CAMPAIGN.SCHEDULED.SECTION_TITLE') }}
          </p>

          <article
            v-for="item in unifiedItems"
            :key="item.key"
            class="rounded-2xl bg-n-solid-1 overflow-hidden"
          >
            <button
              type="button"
              class="w-full flex items-center gap-3.5 px-[18px] py-[15px] text-left hover:bg-n-alpha-2/40 transition-colors"
              @click="toggleExpand(item.key)"
            >
              <div
                class="size-[42px] rounded-[var(--workspace-radius,13px)] bg-n-alpha-2 flex items-center justify-center shrink-0 text-n-slate-11"
              >
                <span class="size-5" :class="item.icon" />
              </div>

              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2 flex-wrap">
                  <span class="text-[15px] font-bold text-n-slate-12 truncate">
                    {{ item.title }}
                  </span>
                  <span
                    class="inline-flex items-center h-6 px-2 rounded-full bg-n-alpha-2 text-xs font-medium text-n-slate-11"
                  >
                    {{ item.statusLabel }}
                  </span>
                </div>
                <div
                  v-if="item.meta"
                  class="mt-1 text-[12.5px] text-n-slate-11 truncate"
                >
                  {{ item.meta }}
                </div>
              </div>

              <span
                class="inline-flex items-center gap-1.5 shrink-0 text-[12.5px] font-bold text-n-blue-11 bg-n-blue-3/40 rounded-full px-2.5 py-1 tabular-nums"
              >
                <span class="i-ri-calendar-line size-3" />
                {{ formatWhen(item.scheduledAt) }}
              </span>

              <span
                class="shrink-0 size-5 text-n-slate-10"
                :class="
                  isExpanded(item.key)
                    ? 'i-ri-arrow-up-s-line'
                    : 'i-ri-arrow-down-s-line'
                "
              />
            </button>

            <div
              v-show="isExpanded(item.key)"
              class="border-t border-n-weak px-[18px] py-4 space-y-3"
            >
              <div class="rounded-2xl bg-n-alpha-2 p-3">
                <p
                  class="text-[13px] font-medium uppercase tracking-wide text-n-slate-11 mb-1"
                >
                  {{ t('SCHEDULE_MESSAGES.LIST.MESSAGE_LABEL') }}
                </p>
                <p
                  class="text-sm text-n-slate-12 whitespace-pre-wrap break-words"
                >
                  {{ item.preview || '—' }}
                </p>
              </div>

              <div class="flex flex-wrap gap-2">
                <Button
                  variant="faded"
                  color="slate"
                  size="sm"
                  icon="i-ri-external-link-line"
                  :label="
                    item.kind === 'message'
                      ? t('SCHEDULE_MESSAGES.LIST.OPEN_CONVERSATION')
                      : t('CAMPAIGN.SCHEDULED.OPEN_CAMPAIGN')
                  "
                  @click="goToOrigin(item)"
                />
                <Button
                  v-if="canCancel(item)"
                  variant="faded"
                  color="ruby"
                  size="sm"
                  :icon="
                    deletesCampaign(item)
                      ? 'i-ri-delete-bin-line'
                      : 'i-ri-forbid-line'
                  "
                  :label="cancelActionLabel(item)"
                  @click="openCancelModal(item)"
                />
              </div>
            </div>
          </article>
        </div>
      </div>
    </main>

    <Dialog
      ref="cancelDialogRef"
      type="alert"
      data-testid="scheduled-cancel-dialog"
      :title="cancelDialogCopy.title"
      :description="cancelDialogCopy.description"
      :confirm-button-label="cancelDialogCopy.confirm"
      :cancel-button-label="t('SCHEDULE_MESSAGES.DELETE_MODAL.KEEP')"
      :is-loading="cancelling"
      @confirm="confirmCancel"
      @close="closeCancelModal"
    />
  </section>
</template>
